import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../components/ui/Modal.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input, Select } from '../../components/ui/Field.jsx'
import api from '../../lib/api.js'
import { useCountries } from '../../lib/useCountries.js'
import { isValidVat, cleanVat, suggestBelgianVat } from '../../lib/validation.js'

/*
 * Create/edit modal for a customer or supplier (the new design uses a modal —
 * prototype `ClientModal`). Field set driven by `config` (customers have payment
 * terms + preferred language; suppliers don't). VAT is cleaned + validated on
 * blur. Internal modal, never a browser dialog.
 */
const LANGS = ['fr', 'en', 'nl']
const empty = {
  name: '', vat_number: '', non_vat_liable: false,
  contact_first_name: '', contact_last_name: '', email: '', phone: '',
  address: '', address_line2: '', postal_code: '', city: '', country: 'BE',
  payment_terms_days: '', preferred_language: 'fr',
}

export default function PartyFormModal({ open, config, entityId, initial, onClose, onSaved }) {
  const { t } = useTranslation()
  const countries = useCountries()
  const [form, setForm] = useState(empty)
  const [vatTouched, setVatTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const isEdit = !!entityId

  useEffect(() => {
    if (!open) return
    setForm(initial ? { ...empty, ...initial, payment_terms_days: initial.payment_terms_days ?? '' } : empty)
    setVatTouched(false)
    setError('')
  }, [open, initial])

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  const vatInvalid = vatTouched && form.vat_number && !isValidVat(form.vat_number)
  // Right shape but wrong BE check digits → there's exactly one valid correction.
  const vatSuggestion = vatInvalid ? suggestBelgianVat(form.vat_number) : null

  const submit = async (e) => {
    e.preventDefault()
    setError('')
    if (form.vat_number && !isValidVat(form.vat_number)) { setVatTouched(true); return }
    setSubmitting(true)
    const payload = {
      ...form,
      vat_number: cleanVat(form.vat_number),
      payment_terms_days: config.hasPaymentTerms && form.payment_terms_days !== '' ? Number(form.payment_terms_days) : undefined,
    }
    if (!config.hasPaymentTerms) delete payload.payment_terms_days
    if (!config.hasLanguage) delete payload.preferred_language
    try {
      const { data } = isEdit
        ? await api.put(`${config.endpoint}/${entityId}`, payload)
        : await api.post(`${config.endpoint}/`, payload) // trailing slash avoids a 308 on POST
      onSaved?.(data?.[config.key === 'customers' ? 'customer' : 'supplier'] || data)
      onClose()
    } catch (err) {
      setError(err?.response?.data?.error || t('parties.toast.error'))
    } finally {
      setSubmitting(false)
    }
  }

  const vatLabel = form.non_vat_liable ? t('parties.fields.companyNumber') : t('parties.fields.vatNumber')

  return (
    <Modal open={open} onClose={onClose} maxWidth={620} closeOnBackdrop={false}>
      <form onSubmit={submit}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 24px 4px' }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, color: 'var(--color-ink)' }}>
            {isEdit ? t(`parties.${config.key}.editTitle`) : t(`parties.${config.key}.newTitle`)}
          </h3>
          <button type="button" onClick={onClose} aria-label={t('common.cancel')} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)' }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ padding: '8px 24px 4px', maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div role="alert" style={{ background: 'var(--color-danger-bg)', color: 'var(--color-danger)', border: '1px solid #f3c9c9', borderRadius: 10, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

          <Field label={t('parties.fields.name')} required>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={vatLabel} error={vatInvalid ? t('parties.fields.vatInvalid') : undefined}>
              <Input value={form.vat_number} onBlur={() => setVatTouched(true)} onChange={(e) => set('vat_number', e.target.value)} placeholder="BE0123456789"
                style={vatInvalid ? { borderColor: 'var(--color-danger)' } : undefined} />
              {vatSuggestion && (
                <button type="button" onClick={() => set('vat_number', vatSuggestion)}
                  style={{ marginTop: 5, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12.5, color: 'var(--color-primary)', fontWeight: 600, textAlign: 'left' }}>
                  {t('parties.fields.vatSuggestion', { vat: vatSuggestion })}
                </button>
              )}
            </Field>
            <Field label={t('parties.fields.email')}>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
            </Field>
          </div>

          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, marginBottom: 14 }}>
            <input type="checkbox" checked={form.non_vat_liable} onChange={(e) => set('non_vat_liable', e.target.checked)} />
            {t('parties.fields.nonVatLiable')}
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={t('parties.fields.contactFirstName')}><Input value={form.contact_first_name} onChange={(e) => set('contact_first_name', e.target.value)} /></Field>
            <Field label={t('parties.fields.contactLastName')}><Input value={form.contact_last_name} onChange={(e) => set('contact_last_name', e.target.value)} /></Field>
            <Field label={t('parties.fields.phone')}><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
            {config.hasPaymentTerms && (
              <Field label={t('parties.fields.paymentTermsDays')} hint={t('parties.form.paymentTermsHint')}>
                <Input type="number" min="0" value={form.payment_terms_days} onChange={(e) => set('payment_terms_days', e.target.value)} />
              </Field>
            )}
          </div>

          <Field label={t('parties.fields.address')}><Input value={form.address} onChange={(e) => set('address', e.target.value)} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 14 }}>
            <Field label={t('parties.fields.postalCode')}><Input value={form.postal_code} onChange={(e) => set('postal_code', e.target.value)} /></Field>
            <Field label={t('parties.fields.city')}><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
            <Field label={t('parties.fields.country')}>
              <Select value={form.country} onChange={(e) => set('country', e.target.value)}>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </Select>
            </Field>
          </div>

          {config.hasLanguage && (
            <Field label={t('parties.fields.language')}>
              <div style={{ display: 'flex', gap: 8 }}>
                {LANGS.map((l) => (
                  <button key={l} type="button" onClick={() => set('preferred_language', l)} style={{
                    flex: 1, height: 40, borderRadius: 'var(--radius-field)', fontSize: 13, fontWeight: 600,
                    border: '1.5px solid ' + (form.preferred_language === l ? 'var(--color-primary)' : 'var(--color-border-strong)'),
                    background: form.preferred_language === l ? 'var(--color-bg-lilac-strong)' : '#fff',
                    color: form.preferred_language === l ? 'var(--color-primary)' : 'var(--color-text)',
                  }}>{l.toUpperCase()}</button>
                ))}
              </div>
            </Field>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 24px 22px', borderTop: '1px solid var(--color-border-row)', marginTop: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" icon="check" loading={submitting}>{t('common.save')}</Button>
        </div>
      </form>
    </Modal>
  )
}
